import React, { useMemo } from 'react';

import { TFunction } from 'i18next';
import flowRight from 'lodash/flowRight';

import { RouteItemType } from '~/core/router';
import {
  withHomeScreenRetentionLayout,
  withLiveNotificationLayout,
  withMainLayout,
  withMainToolsLayout,
  withOnboardingLayout,
  withPromoModalLayout,
  withSubModalsLayout,
} from '~/decorators/withLayout';
import lazyComponentLoader from '~/helpers/lazyComponentLoader';
import {
  AdvancedArticleGeneratorPath,
  AiDetectorPath,
  AiHumanizerPath,
  AiResearcherPath,
  AiResearcherV2Path,
  FactCheckerPath,
  FileConverterPath,
  getAdvancedArticleGeneratorTitle,
  getAiDetectorTitle,
  getAiHumanizerTitle,
  getFactCheckerTitle,
  getPaperWriterTitle,
  getParaphraseTextTitle,
  getPdfConverterTitle,
  getPlagiarismCheckerTitle,
  getQuizGeneratorTittle,
  getRewriteArticleTitle,
  getSummariseTextTitle,
  getToolsGalleryTitle,
  getToolTitle,
  PaperWriterPath,
  ParaphraseTextPath,
  PlagiarismCheckerPath,
  QuizGeneratorPath,
  RewriteArticlePath,
  SummariseTextPath,
  ToolPath,
  ToolsGalleryPath,
} from '~/modules/tools/constants';
import { TOOLS_PROJECT_CONFIG } from '~/modules/tools/projects';
import {
  GB_FEATURE_KEYS,
  GB_FEATURES,
  LiveUserNotificationPlaces,
} from '~/services/abTests/features';

const AiResearcher = lazyComponentLoader(
  () => import('./views/AiResearcherV2'),
);

const ToolsGallery = lazyComponentLoader(() => import('./views/ToolsGallery'));
const Tool = lazyComponentLoader(() => import('./views/Tool'));

const AdvancedArticleGenerator = lazyComponentLoader(
  () => import('./views/AdvancedArticleGenerator'),
);

const PlagiarismCheckerV4 = lazyComponentLoader(
  () => import('./views/PlagiarismCheckerV4'),
);

const FactChecker = lazyComponentLoader(() => import('./views/FactChecker'));

const QuizGenerator = lazyComponentLoader(
  () => import('./views/QuizGenerator'),
);

const AiDetectorV4 = lazyComponentLoader(() => import('./views/AiDetectorV4'));

const AiHumanizerV4 = lazyComponentLoader(
  () => import('./views/AiHumanizerV4'),
);

const ParaphraseTextV3 = lazyComponentLoader(
  () => import('./views/ParaphraseTextV3'),
);

const SummarizeText = lazyComponentLoader(
  () => import('./views/SummarizeText'),
);

const RewriteArticle = lazyComponentLoader(
  () => import('./views/RewriteArticle'),
);

const FileConverter = lazyComponentLoader(
  () => import('./views/FileConverter/index'),
);

export const ToolsGalleryRoute: RouteItemType = {
  Component: flowRight(
    withPromoModalLayout,
    withSubModalsLayout,
    withOnboardingLayout,
    withHomeScreenRetentionLayout,
    withMainLayout,
  )(ToolsGallery),
  path: ToolsGalleryPath,
  getTitle: getToolsGalleryTitle,
};

export const AdvancedArticleGeneratorRoute: RouteItemType = {
  Component: flowRight(
    withPromoModalLayout,
    withSubModalsLayout,
    withOnboardingLayout,
    withHomeScreenRetentionLayout,
    withMainToolsLayout,
  )(AdvancedArticleGenerator),
  path: AdvancedArticleGeneratorPath,
  getTitle: getAdvancedArticleGeneratorTitle,
};

export const PaperWriterRoute: RouteItemType = {
  Component: flowRight(
    withPromoModalLayout,
    withSubModalsLayout,
    withOnboardingLayout,
    withHomeScreenRetentionLayout,
    withMainToolsLayout,
  )(AdvancedArticleGenerator),
  path: PaperWriterPath,
  getTitle: getPaperWriterTitle,
};

export const FactCheckerRoute: RouteItemType = {
  Component: flowRight(
    withPromoModalLayout,
    withSubModalsLayout,
    withOnboardingLayout,
    withHomeScreenRetentionLayout,
    withMainToolsLayout,
  )(FactChecker),
  path: FactCheckerPath,
  getTitle: getFactCheckerTitle,
};

export const QuizGeneratorRoute: RouteItemType = {
  Component: flowRight(
    withPromoModalLayout,
    withSubModalsLayout,
    withOnboardingLayout,
    withHomeScreenRetentionLayout,
    withMainToolsLayout,
  )(QuizGenerator),
  path: QuizGeneratorPath,
  getTitle: getQuizGeneratorTittle,
};

export const FileConverterRoute: RouteItemType = {
  Component: flowRight(
    withPromoModalLayout,
    withSubModalsLayout,
    withOnboardingLayout,
    withHomeScreenRetentionLayout,
    withMainLayout,
  )(FileConverter),
  path: FileConverterPath,
  getTitle: getPdfConverterTitle,
  checkAvailability: (profile) => {
    const variant =
      profile.backend_ab_tests[GB_FEATURE_KEYS.EXPERIMENT_FILE_CONVERTER];

    return (
      variant !==
      GB_FEATURES[GB_FEATURE_KEYS.EXPERIMENT_FILE_CONVERTER].variants.control
    );
  },
};

export const PlagiarismCheckerRoute: RouteItemType = {
  Component: () => {
    const WrappedComponent = useMemo(() => {
      const layouts = [
        withPromoModalLayout,
        withSubModalsLayout,
        withOnboardingLayout,
        withLiveNotificationLayout(LiveUserNotificationPlaces.CHECK_PLAGIARISM),
        withHomeScreenRetentionLayout,
        withMainToolsLayout,
      ];

      return flowRight(...layouts)(PlagiarismCheckerV4);
    }, []);

    return <WrappedComponent />;
  },
  path: PlagiarismCheckerPath,
  getTitle: getPlagiarismCheckerTitle,
};

export const AiDetectorRoute: RouteItemType = {
  Component: () => {
    const WrappedComponent = useMemo(() => {
      const layouts = [
        withPromoModalLayout,
        withSubModalsLayout,
        withOnboardingLayout,
        withLiveNotificationLayout(
          LiveUserNotificationPlaces.AI_CONTENT_DETECTOR,
        ),
        withHomeScreenRetentionLayout,
        withMainToolsLayout,
      ];

      return flowRight(...layouts)(AiDetectorV4);
    }, []);

    return <WrappedComponent />;
  },
  path: AiDetectorPath,
  getTitle: getAiDetectorTitle,
};

export const AiHumanizerRoute: RouteItemType = {
  Component: () => {
    const WrappedComponent = useMemo(() => {
      const layouts = [
        withPromoModalLayout,
        withSubModalsLayout,
        withOnboardingLayout,
        withLiveNotificationLayout(
          LiveUserNotificationPlaces.AI_TEXT_HUMANIZER,
        ),
        withHomeScreenRetentionLayout,
        withMainToolsLayout,
      ];

      return flowRight(...layouts)(AiHumanizerV4);
    }, []);

    return <WrappedComponent />;
  },
  path: AiHumanizerPath,
  getTitle: getAiHumanizerTitle,
};

export const ParaphraseTextRoute: RouteItemType = {
  Component: flowRight(
    withPromoModalLayout,
    withSubModalsLayout,
    withOnboardingLayout,
    withHomeScreenRetentionLayout,
    withMainToolsLayout,
  )(ParaphraseTextV3),
  path: ParaphraseTextPath,
  getTitle: getParaphraseTextTitle,
};

export const SummarizeTextRoute: RouteItemType = {
  Component: flowRight(
    withPromoModalLayout,
    withSubModalsLayout,
    withOnboardingLayout,
    withHomeScreenRetentionLayout,
    withMainToolsLayout,
  )(SummarizeText),
  path: SummariseTextPath,
  getTitle: getSummariseTextTitle,
};

export const RewriteArticleRoute: RouteItemType = {
  Component: flowRight(
    withPromoModalLayout,
    withSubModalsLayout,
    withOnboardingLayout,
    withHomeScreenRetentionLayout,
    withMainToolsLayout,
  )(RewriteArticle),
  path: RewriteArticlePath,
  getTitle: getRewriteArticleTitle,
};

export const AiResearcherRoute: RouteItemType = {
  Component: flowRight(
    withPromoModalLayout,
    withSubModalsLayout,
    withOnboardingLayout,
    withHomeScreenRetentionLayout,
    withMainToolsLayout,
  )(AiResearcher),
  path: AiResearcherPath,
  getTitle: (t: TFunction): string =>
    TOOLS_PROJECT_CONFIG.getAiResearcherTitle(t),
};

export const ToolRoute: RouteItemType = {
  Component: flowRight(
    withPromoModalLayout,
    withSubModalsLayout,
    withOnboardingLayout,
    withHomeScreenRetentionLayout,
    withMainToolsLayout,
  )(Tool),
  path: ToolPath,
  getTitle: getToolTitle,
};
